// One unit of a retail park as a plan: the box, its structural grid, the
// shopfront under the canopy at the bottom, the yard with the docks at the
// top, and the dimensions a tenant's agent asks for first (frontage, depth,
// lettable area, docks and their apron).
import { distanceTo, type Pt } from "../geom.ts";
import { DOCK_APRON } from "../../rules/ro.ts";
import { toLocal, type RetailUnit, type Scheme } from "./masterplan.ts";
import { KIND_NAME, type RetailProgramme } from "./programme.ts";

const fmt = (n: number, dp = 1) => n.toLocaleString("ro-RO", { maximumFractionDigits: dp, minimumFractionDigits: dp });
const esc = (t: string) => t.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
export const VEHICLE_NAME = { articulated: "TIR articulat", rigid: "camion rigid", van: "dubă" } as const;

export function unitPlanSvg(u: RetailUnit, s: Scheme, prog: Pick<RetailProgramme, "canopyDepth">, width = 760): string {
  const f = s.spines[u.wing].frame;
  const L = (p: Pt) => toLocal(f, p);
  const ring = u.polygon[0][0].map(L);
  const xs = ring.map((p) => p[0]), ts = ring.map((p) => p[1]);
  const s0 = Math.min(...xs), s1 = Math.max(...xs), tB = Math.min(...ts), tF = Math.max(...ts);
  const yard = 8, below = prog.canopyDepth + 12, side = 6;
  const W = s1 - s0 + 2 * side, H = tF - tB + yard + below;
  const k = width / W;
  const height = Math.round(H * k + 40);
  // s to the right, t downwards: the yard on top, the shopfront at the bottom
  const X = (sv: number) => (sv - s0 + side) * k;
  const Y = (tv: number) => (tv - tB + yard) * k + 10;
  const P = (p: Pt) => `${X(p[0]).toFixed(1)},${Y(p[1]).toFixed(1)}`;
  const out: string[] = [];
  out.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" font-family="system-ui, sans-serif">`);
  out.push(`<rect width="${width}" height="${height}" fill="#fff"/>`);
  // yard and canopy bands
  out.push(`<rect x="0" y="${Y(tB - yard)}" width="${width}" height="${yard * k}" fill="#d6d9de"/>`);
  out.push(`<text x="8" y="${Y(tB - yard) + 16}" font-size="12" fill="#4b5563">curte de livrare</text>`);
  if (prog.canopyDepth > 0) {
    out.push(`<rect x="${X(s0)}" y="${Y(tF)}" width="${(s1 - s0) * k}" height="${prog.canopyDepth * k}" fill="#eef2f7" stroke="#9aa5b4" stroke-dasharray="4 3"/>`);
    out.push(`<text x="${X(s0) + 6}" y="${Y(tF) + prog.canopyDepth * k / 2 + 4}" font-size="11" fill="#4b5563">copertină ${fmt(prog.canopyDepth)} m</text>`);
  }
  out.push(`<text x="8" y="${Y(tF + prog.canopyDepth) + 18}" font-size="12" fill="#4b5563">aleea din față · parcare</text>`);
  // the box
  out.push(`<polygon points="${ring.map(P).join(" ")}" fill="#f6efe2" stroke="#1f2937" stroke-width="${Math.max(2, 0.3 * k)}"/>`);
  // the structural grid; a column on a party wall belongs to both units
  for (const c of s.columns) {
    if (distanceTo(c, u.polygon) > 0.25) continue;
    const [cs, ct] = L(c);
    const r = Math.max(2.5, 0.25 * k);
    out.push(`<rect x="${(X(cs) - r).toFixed(1)}" y="${(Y(ct) - r).toFixed(1)}" width="${2 * r}" height="${2 * r}" fill="#374151"/>`);
  }
  // shopfront and entrance
  const [fa, fb] = [L(u.front[0]), L(u.front[1])];
  out.push(`<line x1="${X(fa[0])}" y1="${Y(tF)}" x2="${X(fb[0])}" y2="${Y(tF)}" stroke="#38bdf8" stroke-width="${Math.max(4, 0.35 * k)}"/>`);
  const [es] = L(u.entrance);
  out.push(`<path d="M ${X(es)} ${Y(tF) + 24} l -7 10 h 14 z" fill="#16a34a"/><text x="${X(es)}" y="${Y(tF) + 48}" font-size="11" text-anchor="middle" fill="#166534">intrare</text>`);
  // docks and doors on the back wall
  for (const o of u.service) {
    const [a] = L(o.a), [b] = L(o.b);
    const x0 = X(Math.min(a, b)), w = Math.abs(b - a) * k;
    if (o.kind === "dock") {
      out.push(`<rect x="${x0}" y="${Y(tB) - 3}" width="${w}" height="${Math.max(6, 0.6 * k)}" fill="#7c2d12"/>`);
      out.push(`<rect x="${x0 - 2}" y="${Y(tB) - 3 - 2.5 * k}" width="${w + 4}" height="${2.5 * k}" fill="none" stroke="#7c2d12" stroke-dasharray="3 2"/>`);

    } else {
      out.push(`<rect x="${x0}" y="${Y(tB) - 2}" width="${Math.max(3, w)}" height="${Math.max(5, 0.4 * k)}" fill="#d97706"/>`);
    }
  }
  // one note over the docks: how many, and the yard in front of the shortest
  const docks = u.service.filter((o) => o.kind === "dock");
  if (docks.length) {
    const mids = docks.map((o) => (L(o.a)[0] + L(o.b)[0]) / 2);
    const worst = docks.reduce((x, y) => (y.apron < x.apron ? y : x));
    const ok = worst.apron >= DOCK_APRON[worst.vehicle] - 0.1;
    out.push(`<text x="${X((Math.min(...mids) + Math.max(...mids)) / 2)}" y="${Y(tB) - 3 - 2.5 * k - 5}" font-size="11" text-anchor="middle" fill="${ok ? "#7c2d12" : "#b91c1c"}">${docks.length === 1 ? "cheu" : `${docks.length} cheiuri`} · ${fmt(worst.apron, 1)} m liber</text>`);
  }
  // dimensions, below the entrance mark
  const dimY = Y(tF + prog.canopyDepth) + 62;
  out.push(`<g stroke="#111" stroke-width="1"><line x1="${X(s0)}" y1="${dimY}" x2="${X(s1)}" y2="${dimY}"/><line x1="${X(s0)}" y1="${dimY - 5}" x2="${X(s0)}" y2="${dimY + 5}"/><line x1="${X(s1)}" y1="${dimY - 5}" x2="${X(s1)}" y2="${dimY + 5}"/></g>`);
  out.push(`<text x="${(X(s0) + X(s1)) / 2}" y="${dimY - 5}" font-size="12" text-anchor="middle">${fmt(s1 - s0, 2)} m front</text>`);
  const dimX = X(s1) + 18;
  out.push(`<g stroke="#111" stroke-width="1"><line x1="${dimX}" y1="${Y(tB)}" x2="${dimX}" y2="${Y(tF)}"/><line x1="${dimX - 5}" y1="${Y(tB)}" x2="${dimX + 5}" y2="${Y(tB)}"/><line x1="${dimX - 5}" y1="${Y(tF)}" x2="${dimX + 5}" y2="${Y(tF)}"/></g>`);
  out.push(`<text transform="translate(${dimX + 14} ${(Y(tB) + Y(tF)) / 2}) rotate(-90)" font-size="12" text-anchor="middle">${fmt(tF - tB, 2)} m adâncime</text>`);
  // the unit's name in the middle
  const cy = (Y(tB) + Y(tF)) / 2, cx = (X(s0) + X(s1)) / 2;
  out.push(`<text x="${cx}" y="${cy - 8}" font-size="18" font-weight="600" text-anchor="middle">${esc(u.id)} · ${esc(KIND_NAME[u.kind])}</text>`);
  out.push(`<text x="${cx}" y="${cy + 14}" font-size="14" text-anchor="middle">${fmt(u.gla, 0)} m² GLA</text>`);
  // scale bar and north
  const bar = 10 * k;
  out.push(`<g transform="translate(12 ${height - 16})"><rect width="${bar / 2}" height="5" fill="#111"/><rect x="${bar / 2}" width="${bar / 2}" height="5" fill="#fff" stroke="#111"/><text x="${bar + 6}" y="6" font-size="10">10 m</text></g>`);
  const north = [f.u[1], f.n[1]]; // world +y in local (s, t)
  const nl = Math.hypot(north[0], north[1]) || 1;
  const ax = width - 30, ay = height - 30;
  out.push(`<g transform="translate(${ax} ${ay}) rotate(${(Math.atan2(north[0] / nl, -north[1] / nl) * 180) / Math.PI})"><path d="M0 -14 L6 8 L0 4 L-6 8 Z" fill="#111"/><text y="-18" font-size="10" text-anchor="middle">N</text></g>`);
  out.push(`</svg>`);
  return out.join("");
}

/** The rows of a unit's card: what it is, what it lets for, what serves it. */
export function unitSummary(u: RetailUnit): { label: string; value: string }[] {
  const docks = u.service.filter((o) => o.kind === "dock");
  return [
    { label: "Tip", value: KIND_NAME[u.kind] },
    { label: "Suprafață construită", value: `${fmt(u.area, 0)} m²` },
    { label: "Suprafață închiriabilă (GLA)", value: `${fmt(u.gla, 0)} m²` },
    { label: "Front la parcare", value: `${fmt(u.frontage, 2)} m` },
    { label: "Adâncime", value: `${fmt(u.depth, 2)} m` },
    { label: "Cheiuri de descărcare", value: docks.length ? `${docks.length} · ${VEHICLE_NAME[docks[0].vehicle]}` : "fără (ușă de serviciu)" },
    ...docks.map((d, i) => ({ label: `Curte liberă la cheiul ${i + 1}`, value: `${fmt(d.apron, 1)} m (necesar ${DOCK_APRON[d.vehicle]} m)` })),
  ];
}
